/**
 * S-1006: Modal Manager Hook - React integration for centralized modal management
 * Provides React hooks for managing modal state with race condition prevention
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { modalManager, ModalConfig, ModalEvent, ModalType } from '@/lib/modals/ModalManager';

export interface UseModalManagerOptions {
  modalId: string;
  type: ModalType;
  priority?: number;
  canStack?: boolean;
  blockingLevel?: 'none' | 'same-type' | 'all';
  onClose?: () => void;
  metadata?: Record<string, any>;
}

export interface UseModalManagerReturn {
  isOpen: boolean;
  canOpen: boolean;
  open: () => boolean;
  close: (immediate?: boolean) => void;
  isTopModal: boolean;
  modalStack: ModalConfig[];
  zIndex: number;
}

/**
 * Hook for managing individual modal state through ModalManager
 */
export function useModalManager(options: UseModalManagerOptions): UseModalManagerReturn {
  const {
    modalId,
    type,
    priority = 0,
    canStack = true,
    blockingLevel = 'none',
    onClose,
    metadata
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [canOpen, setCanOpen] = useState(true);
  const [isTopModal, setIsTopModal] = useState(false);
  const [modalStack, setModalStack] = useState<ModalConfig[]>([]);
  const [zIndex, setZIndex] = useState(1000);

  const configRef = useRef<ModalConfig>();
  const isOpenRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Create modal config
  useEffect(() => {
    configRef.current = {
      id: modalId,
      type,
      priority,
      canStack,
      blockingLevel,
      escapeKeyHandler: () => {
        // Use modalManager state instead of potentially stale ref
        if (modalManager.isModalOpen(modalId) && onClose) {
          onClose();
        }
      },
      onClose,
      metadata
    };
  }, [modalId, type, priority, canStack, blockingLevel, onClose, metadata]);

  // Listen to modal manager events with proper synchronization
  useEffect(() => {
    const handleModalEvent = (event: ModalEvent) => {
      // Use requestAnimationFrame to ensure React state updates are synchronized
      requestAnimationFrame(() => {
        if (event.modalId === modalId) {
          switch (event.type) {
            case 'opened':
              setIsOpen(true);
              setZIndex(event.metadata?.zIndex || 1000);
              break;
            case 'closed':
              setIsOpen(false);
              break;
            case 'blocked':
              setCanOpen(false);
              // Reset canOpen after a delay
              setTimeout(() => setCanOpen(true), 1000);
              break;
          }
        }

        // Update modal stack and top modal status
        const stack = modalManager.getModalStack();
        setModalStack(stack);
        
        const topModal = modalManager.getTopModal();
        setIsTopModal(topModal?.id === modalId);
      });
    };

    const removeListener = modalManager.addEventListener(handleModalEvent);
    
    // Initial state sync with proper timing
    requestAnimationFrame(() => {
      setIsOpen(modalManager.isModalOpen(modalId));
      setModalStack(modalManager.getModalStack());
      const topModal = modalManager.getTopModal();
      setIsTopModal(topModal?.id === modalId);
    });

    return removeListener;
  }, [modalId]);

  /**
   * AC1: Open modal with race condition prevention
   */
  const open = useCallback((): boolean => {
    if (!configRef.current) {
      console.error(`[useModalManager] Config not ready for modal: ${modalId}`);
      return false;
    }

    const success = modalManager.registerModal(configRef.current);
    
    if (!success) {
      console.log(`[useModalManager] Modal blocked: ${modalId}`);
      setCanOpen(false);
      // Reset canOpen after a delay
      setTimeout(() => setCanOpen(true), 1000);
    }

    return success;
  }, [modalId]);

  /**
   * AC2: Close modal with cleanup
   */
  const close = useCallback((immediate = false): void => {
    modalManager.unregisterModal(modalId, immediate);
  }, [modalId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isOpenRef.current) {
        modalManager.unregisterModal(modalId, true);
      }
    };
  }, [modalId]);

  return {
    isOpen,
    canOpen,
    open,
    close,
    isTopModal,
    modalStack,
    zIndex
  };
}

/**
 * Hook for managing modal focus and accessibility
 */
export function useModalFocus(modalId: string, isOpen: boolean) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const focusTimeoutRef = useRef<number | null>(null);

  // AC4: Focus management with race condition prevention
  useEffect(() => {
    if (isOpen) {
      // Store previous focus
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // S-2203: Better synchronization with Radix UI focus management
      const focusModal = () => {
        // Double-check modal is still open to prevent race conditions
        if (!modalManager.isModalOpen(modalId)) return;
        
        if (modalRef.current) {
          // S-2203: Let Radix UI handle initial focus, only intervene if needed
          const radixFocusedElement = modalRef.current.querySelector('[data-radix-dialog-content] [autofocus], [data-radix-alert-dialog-content] [autofocus]');
          
          if (!radixFocusedElement) {
            // Only set focus if Radix UI hasn't already handled it
            const focusableElement = modalRef.current.querySelector(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
            ) as HTMLElement;
            
            if (focusableElement) {
              focusableElement.focus();
            } else if (modalRef.current.tabIndex >= 0) {
              modalRef.current.focus();
            }
          }
        }
      };

      // S-2203: Use longer delay to ensure Radix UI animations complete
      focusTimeoutRef.current = requestAnimationFrame(() => {
        focusTimeoutRef.current = requestAnimationFrame(() => {
          // Additional frame for Radix UI to complete its focus management
          focusTimeoutRef.current = requestAnimationFrame(focusModal);
        });
      });

      return () => {
        if (focusTimeoutRef.current) {
          cancelAnimationFrame(focusTimeoutRef.current);
        }
      };
    } else {
      // S-2203: Restore previous focus with better timing and validation
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        // Wait for modal close animation to complete
        const restoreFocus = () => {
          if (previousFocusRef.current && 
              document.contains(previousFocusRef.current) && 
              !modalManager.isModalOpen(modalId)) {
            try {
              previousFocusRef.current.focus();
            } catch (error) {
              // S-2203: Graceful fallback if focus restoration fails
              console.warn('[useModalFocus] Failed to restore focus:', error);
            }
          }
        };

        // S-2203: Delay to match modal close animation timing
        setTimeout(restoreFocus, 150);
      }
    }
  }, [isOpen, modalId]);

  return { modalRef };
}

/**
 * Hook for global modal state monitoring
 */
export function useModalState() {
  const [openModals, setOpenModals] = useState<ModalConfig[]>([]);
  const [topModal, setTopModal] = useState<ModalConfig | null>(null);

  useEffect(() => {
    const handleModalEvent = () => {
      // Synchronize state updates with React rendering cycle
      requestAnimationFrame(() => {
        setOpenModals(modalManager.getOpenModals());
        setTopModal(modalManager.getTopModal());
      });
    };

    const removeListener = modalManager.addEventListener(handleModalEvent);
    
    // Initial state with proper timing
    requestAnimationFrame(() => {
      setOpenModals(modalManager.getOpenModals());
      setTopModal(modalManager.getTopModal());
    });

    return removeListener;
  }, []);

  return {
    openModals,
    topModal,
    hasOpenModals: openModals.length > 0,
    modalCount: openModals.length
  };
}

/**
 * Hook for emergency modal cleanup
 */
export function useModalCleanup() {
  const forceCloseAll = useCallback(() => {
    modalManager.closeAllModals();
  }, []);

  // Cleanup on critical errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[useModalCleanup] Critical error detected, cleaning up modals:', event.error);
      forceCloseAll();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[useModalCleanup] Unhandled promise rejection, cleaning up modals:', event.reason);
      forceCloseAll();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [forceCloseAll]);

  return { forceCloseAll };
}
